/**
 * TDS Compliance Center — workflow model + interim local store.
 *
 * PHASE 1 (frontend-only). The compliance lifecycle state for stages that have
 * no backend yet (deposit, challans, return generation, upload, acknowledgement,
 * Form16) is tracked in localStorage, keyed by org + financial year + quarter,
 * with an immutable-style append-only audit log. In Phase 2 this store is
 * swapped for real APIs without changing the components that consume it.
 */

import { useCallback, useEffect, useState } from "react";

export const QUARTERS = [
  { id: "Q1", label: "Q1", period: "Apr – Jun", months: ["Apr", "May", "Jun"], dueDay: "31 Jul" },
  { id: "Q2", label: "Q2", period: "Jul – Sep", months: ["Jul", "Aug", "Sep"], dueDay: "31 Oct" },
  { id: "Q3", label: "Q3", period: "Oct – Dec", months: ["Oct", "Nov", "Dec"], dueDay: "31 Jan" },
  { id: "Q4", label: "Q4", period: "Jan – Mar", months: ["Jan", "Feb", "Mar"], dueDay: "31 May" },
];

export const STAGES = [
  { key: "payroll", n: 1, title: "Payroll Completed", short: "Payroll" },
  { key: "deposit", n: 2, title: "Deposit Tax", short: "Deposit" },
  { key: "challan", n: 3, title: "Challan Entry", short: "Challan" },
  { key: "generate", n: 4, title: "Generate 24Q", short: "24Q" },
  { key: "download", n: 5, title: "Download Return", short: "Download" },
  { key: "upload", n: 6, title: "Upload to Portal", short: "Upload" },
  { key: "ack", n: 7, title: "Acknowledgement", short: "Ack" },
  { key: "form16", n: 8, title: "Generate Form 16", short: "Form 16" },
];

export function currentFinancialYear(d = new Date()) {
  const y = d.getFullYear();
  const m = d.getMonth(); // 0=Jan
  const start = m >= 3 ? y : y - 1; // FY starts in April
  return `${start}-${String(start + 1).slice(-2)}`;
}

export function currentQuarter(d = new Date()) {
  const m = d.getMonth();
  if (m >= 3 && m <= 5) return "Q1";
  if (m >= 6 && m <= 8) return "Q2";
  if (m >= 9 && m <= 11) return "Q3";
  return "Q4";
}

function emptyQuarterState() {
  return {
    deposit: { status: "pending", paidAmount: 0, paidDate: "", note: "" },
    challans: [],
    return: { generated: false, generatedAt: "", generatedBy: "", version: 0, validationStatus: "" },
    download: { downloaded: false, lastAt: "" },
    upload: { status: "waiting", note: "", date: "" },
    ack: { status: "pending", ackNumber: "", tokenNumber: "", uploadDate: "", acceptedDate: "", remarks: "" },
    form16: { generated: false, generatedAt: "", generatedBy: "", emailStatus: "not_sent" },
    audit: [],
  };
}

const STORE_KEY = "tds_compliance_store_v1";

function readStore() {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) || "{}") || {};
  } catch {
    return {};
  }
}
function writeStore(data) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(data));
  } catch {
    /* ignore quota */
  }
}

/**
 * Hook giving the quarter state + mutators for a given FY/quarter.
 * actor is used to stamp the audit log.
 */
export function useTdsComplianceStore(fy, quarter, actor = "System Admin") {
  const [version, setVersion] = useState(0); // re-render trigger
  const [state, setState] = useState(() => {
    const all = readStore();
    return all?.[fy]?.[quarter] || emptyQuarterState();
  });

  useEffect(() => {
    const all = readStore();
    setState(all?.[fy]?.[quarter] || emptyQuarterState());
  }, [fy, quarter, version]);

  const persist = useCallback(
    (mutator, auditEntry) => {
      const all = readStore();
      if (!all[fy]) all[fy] = {};
      const prev = all[fy][quarter] || emptyQuarterState();
      const next = mutator({ ...prev });
      if (auditEntry) {
        next.audit = [
          { ts: new Date().toISOString(), actor, ...auditEntry },
          ...(prev.audit || []),
        ].slice(0, 200);
      }
      all[fy][quarter] = next;
      writeStore(all);
      setVersion((v) => v + 1);
    },
    [fy, quarter, actor]
  );

  const actions = {
    markDeposited: (amount, date, note) =>
      persist(
        (s) => ({ ...s, deposit: { status: "deposited", paidAmount: amount, paidDate: date, note: note || "" } }),
        { action: "Marked tax deposited", detail: `₹${amount} on ${date}` }
      ),
    resetDeposit: () =>
      persist((s) => ({ ...s, deposit: { status: "pending", paidAmount: 0, paidDate: "", note: "" } }), {
        action: "Reset deposit status",
      }),
    addChallan: (challan) =>
      persist(
        (s) => ({ ...s, challans: [...(s.challans || []), { id: `ch_${Date.now()}`, status: "pending", ...challan }] }),
        { action: "Added challan", detail: `${challan.challanNo} · ₹${challan.amount}` }
      ),
    updateChallan: (id, patch) =>
      persist((s) => ({ ...s, challans: (s.challans || []).map((c) => (c.id === id ? { ...c, ...patch } : c)) }), {
        action: "Updated challan",
        detail: id,
      }),
    deleteChallan: (id) =>
      persist((s) => ({ ...s, challans: (s.challans || []).filter((c) => c.id !== id) }), {
        action: "Deleted challan",
        detail: id,
      }),
    generateReturn: (validationStatus) =>
      persist(
        (s) => ({
          ...s,
          return: {
            generated: true,
            generatedAt: new Date().toISOString(),
            generatedBy: actor,
            version: (s.return?.version || 0) + 1,
            validationStatus: validationStatus || "passed",
          },
        }),
        { action: "Generated 24Q return", detail: `v${(state.return?.version || 0) + 1}` }
      ),
    markDownloaded: (kind) =>
      persist((s) => ({ ...s, download: { downloaded: true, lastAt: new Date().toISOString() } }), {
        action: "Downloaded return",
        detail: kind,
      }),
    setUploadStatus: (status, note) =>
      persist((s) => ({ ...s, upload: { status, note: note || "", date: new Date().toISOString() } }), {
        action: "Updated upload status",
        detail: status,
      }),
    saveAck: (ack) =>
      persist((s) => ({ ...s, ack: { ...s.ack, ...ack } }), {
        action: "Saved acknowledgement",
        detail: ack.ackNumber || ack.status || "",
      }),
    setForm16: (patch) =>
      persist((s) => ({ ...s, form16: { ...s.form16, ...patch } }), {
        action: "Form 16 updated",
        detail: patch.generated ? "generated" : patch.emailStatus || "",
      }),
  };

  return { state, actions };
}

/** Total amount covered by challans (any status). */
export function challansTotal(challans) {
  return (challans || []).reduce((sum, c) => sum + (Number(c.amount) || 0), 0);
}

/** Duplicate challan numbers within the quarter. */
export function duplicateChallanNumbers(challans) {
  const seen = {};
  const dupes = new Set();
  for (const c of challans || []) {
    const k = String(c.challanNo || "").trim().toUpperCase();
    if (!k) continue;
    if (seen[k]) dupes.add(k);
    seen[k] = true;
  }
  return [...dupes];
}

/**
 * Derive each stage's status (completed | pending | error | locked) for a quarter.
 * payable = quarter TDS payable (₹).
 */
export function computeStageStatuses(state, { payable = 0, payrollDone = false } = {}) {
  const s = state || emptyQuarterState();
  const chTotal = challansTotal(s.challans);

  const out = {};
  out.payroll = payrollDone ? "completed" : "pending";

  // deposit
  if (out.payroll !== "completed") out.deposit = "locked";
  else out.deposit = s.deposit?.status === "deposited" ? "completed" : "pending";

  // challan
  if (out.deposit !== "completed") out.challan = "locked";
  else if ((s.challans || []).length === 0) out.challan = "pending";
  else if (duplicateChallanNumbers(s.challans).length > 0) out.challan = "error";
  else out.challan = "completed";

  // generate 24Q
  if (out.challan !== "completed") out.generate = "locked";
  else out.generate = s.return?.generated ? "completed" : "pending";

  // download
  if (out.generate !== "completed") out.download = "locked";
  else out.download = s.download?.downloaded ? "completed" : "pending";

  // upload
  if (out.generate !== "completed") out.upload = "locked";
  else if (s.upload?.status === "accepted") out.upload = "completed";
  else if (s.upload?.status === "rejected") out.upload = "error";
  else out.upload = "pending";

  // acknowledgement
  if (out.upload !== "completed") out.ack = "locked";
  else if (s.ack?.status === "filed") out.ack = "completed";
  else if (s.ack?.status === "rejected") out.ack = "error";
  else out.ack = "pending";

  // form16
  if (out.ack !== "completed") out.form16 = "locked";
  else out.form16 = s.form16?.generated ? "completed" : "pending";

  return { statuses: out, challanTotal: chTotal };
}

/** Validation report used before generating the 24Q return. */
export function buildReturnValidation(state, { payable = 0, tanPresent = null, companyPanPresent = null }) {
  const s = state || emptyQuarterState();
  const chTotal = challansTotal(s.challans);
  const dupes = duplicateChallanNumbers(s.challans);
  const checks = [
    { id: "payroll", label: "Payroll processed for the quarter", ok: payable > 0 },
    { id: "deposit", label: "Tax marked as deposited", ok: s.deposit?.status === "deposited" },
    { id: "challan", label: "At least one challan entered", ok: (s.challans || []).length > 0 },
    { id: "dupes", label: "No duplicate challan numbers", ok: dupes.length === 0, detail: dupes.join(", ") },
    {
      id: "match",
      label: "Challan total matches tax payable",
      ok: payable > 0 && Math.abs(chTotal - payable) <= Math.max(1, payable * 0.01),
      detail: payable > 0 ? `Challans ₹${Math.round(chTotal)} vs payable ₹${Math.round(payable)}` : "",
    },
  ];
  // TAN / company PAN — only assert when we actually know
  if (tanPresent !== null) checks.push({ id: "tan", label: "Deductor TAN configured", ok: Boolean(tanPresent) });
  if (companyPanPresent !== null) checks.push({ id: "pan", label: "Company PAN configured", ok: Boolean(companyPanPresent) });

  const passed = checks.every((c) => c.ok);
  return { checks, passed };
}

export function complianceScore(allQuarterStatuses) {
  // allQuarterStatuses: array of statuses objects
  let done = 0;
  let total = 0;
  for (const st of allQuarterStatuses) {
    for (const k of Object.keys(st)) {
      total += 1;
      if (st[k] === "completed") done += 1;
    }
  }
  return total ? Math.round((done / total) * 100) : 0;
}
